import React, { useCallback, useMemo, useState } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Eye, Pencil, Trash2, UserRoundCog } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "sonner";

import { getUserHref, userColumns } from "./tableParams/userColumns";
import UsersCreateButton from "./UsersCreateButton";
import { notifError } from "../../../actions/FlashMessageActions";
import { delApi, getApi } from "../../../actions/SendActions";
import AdminUserAPI from "../../../api/AdminUserAPI";
import {
  apiExportUsersByDates,
  apiUsersImport,
  fetchUsersViewsExport,
} from "../../../api/legacy/UserAPI";
import { API_DATE_FORMAT } from "../../../constants/dateFormat";
import { selectPaginatedPage } from "../../../hooks/selectPaginatedPage";
import useAbility from "../../../hooks/useAbility";
import useDataTableQuery from "../../../hooks/useDataTableQuery";
import useDataTableServerState from "../../../hooks/useDataTableServerState";
import usePeriodFilter from "../../../hooks/usePeriodFilter";
import { SORT_DIRECTION } from "../../../utils/sortingToApi";
import PageTitle from "../../common/PageTitle";
import DataTable from "../../dataTable/DataTable";
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../ui/card";
import InfoPanel from "../../utils/InfoPanel";
import AnalyticsPage from "../AnalyticsPage";
import AnalyticsPeriodPicker from "../charts/AnalyticsPeriodPicker";
import ExportCsvButton from "../charts/ExportCsvButton";
import fetchLegacyExport from "../charts/fetchLegacyExport";
import SignupsAndSubscriptionsSection from "../dashboardAnalytics/signups/SignupsAndSubscriptionsSection";
import CustomerKpiGrid from "../kpi/CustomerKpiGrid";

import type { JSX } from "react";

import type { UserStatusResponse } from "./UsersAdmin.types";
import type { ImportedUser, User } from "../../../api/AdminUserAPI.types";
import type { AppState } from "../../../reducers";
import type { RowAction } from "../../dataTable/dataTable.types";

const USER_API_ENDPOINT = "/api/user/v3/admin/users";

function UsersAdmin(): JSX.Element {
  const { t } = useTranslation(["editor-users", "editor-analytics"]);
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const dispatch = useDispatch();
  const history = useHistory();
  const queryClient = useQueryClient();

  const { period, setPeriod } = usePeriodFilter();

  const token = useSelector((state: AppState) => state.loginReducer.token);
  const orgaOptions = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config.data?.organization_options,
  );
  const { canAccess } = useAbility();
  const { project: canAccessProject } = canAccess(orgaOptions);
  const canWrite = canAccessProject.usersWrite;

  const [importedUsers, setImportedUsers] = useState<ImportedUser[]>([]);
  const [importError, setImportError] = useState<string>("");

  // Pas de `refetch` : les colonnes doivent exister avant la requête.
  const reloadUsers = useCallback((): void => {
    queryClient
      .invalidateQueries([AdminUserAPI.getUserList.name])
      .catch(() => undefined);
  }, [queryClient]);

  const { mutate: impersonateUser } = useMutation({
    mutationFn: async (userId: string) =>
      AdminUserAPI.impersonateUser({ userId, projectSlug }),
    onSuccess: (data) => {
      if (data?.url) window.open(data.url, "_blank");
    },
    onError: (error: unknown) => {
      console.error(error);
      toast.error(t("error:genericError"));
    },
  });

  const deleteUser = useCallback(
    (uuid: string): void => {
      dispatch(
        delApi({
          endpoint: `${USER_API_ENDPOINT}/${uuid}/delete`,
          type: "DELETE_USER",
          item: "users",
          headers: [{ key: "xx-ok-project", value: projectSlug }],
          token,
          message: t("editor-notif:deleteSuccess"),
          callbackError: () => {
            dispatch(notifError(t("editor-notif:deleteError")));
          },
          callback: reloadUsers,
        }),
      );
    },
    [dispatch, projectSlug, token, t, reloadUsers],
  );

  // Statut relu à chaque ouverture du menu ; sans droit d'écriture ou en cas d'échec, il reste « Voir » et « Incarner ».
  const loadActions = useCallback(
    async (user: User): Promise<RowAction[]> => {
      const status: UserStatusResponse | undefined = canWrite
        ? await dispatch(
            getApi({
              endpoint: `${USER_API_ENDPOINT}/${user.uuid}/status`,
              type: "GET_USER_STATUS",
              headers: [{ key: "xx-ok-project", value: projectSlug }],
              token,
            }),
          ).catch(() => undefined)
        : undefined;
      const canEdit = canWrite && status?.is_editable === true;
      const href = getUserHref(user, projectSlug);

      return [
        {
          label: canEdit
            ? t("editor-content:edit_action")
            : t("editor-content:see_more"),
          icon: canEdit ? <Pencil /> : <Eye />,
          hidden: !href,
          onClick: () => {
            if (href) history.push(href);
          },
        },
        {
          label: t("editor-content:impersonate"),
          icon: <UserRoundCog />,
          onClick: () => impersonateUser(user.uuid),
        },
        ...(status?.is_deletable
          ? [
              {
                label: t("editor-content:delete"),
                icon: <Trash2 />,
                variant: "destructive",
                confirm: {
                  title: t("modal:confirm"),
                  description: t("deleteBody", { email: user.email }),
                  confirmLabel: t("modal:submit"),
                },
                onClick: () => deleteUser(user.uuid),
              } satisfies RowAction,
            ]
          : []),
      ];
    },
    [
      canWrite,
      dispatch,
      projectSlug,
      token,
      t,
      history,
      impersonateUser,
      deleteUser,
    ],
  );

  const columnList = useMemo(
    () => userColumns({ t, projectSlug, loadActions }),
    [t, projectSlug, loadActions],
  );

  const serverState = useDataTableServerState<User>({
    columns: columnList,
    defaultSorting: { id: "updated_at", desc: true },
    resetPageOn: [period.startDate.valueOf(), period.endDate.valueOf()],
  });

  const userListQuery = AdminUserAPI.generateQuery({
    fn: AdminUserAPI.getUserList,
    params: [
      {
        page: serverState.page,
        perPage: serverState.perPage,
        search: serverState.search || undefined,
        startDate: period.startDate.toISOString(),
        endDate: period.endDate.toISOString(),
        subscriptionStatus: serverState.filters.subscription_status ?? null,
        order: serverState.order
          ? {
              direction: serverState.order.direction,
              column: serverState.order.field,
            }
          : { direction: SORT_DIRECTION.DESC, column: "updated_at" },
        projectSlug,
      },
    ],
  });

  const userTable = useDataTableQuery(userListQuery, selectPaginatedPage);

  const canExport = !userTable.isLoading && userTable.data.length > 0;

  const fetchUsersCsv = async (): Promise<string> =>
    fetchLegacyExport(period.startDate, (periodStart, onSuccess, onError) => {
      apiExportUsersByDates(
        periodStart.format(API_DATE_FORMAT),
        period.endDate.format(API_DATE_FORMAT),
        projectSlug,
        token,
        onSuccess,
        onError,
      );
    });

  const fetchUsersViewsCsv = async (): Promise<string> =>
    fetchLegacyExport(period.startDate, (periodStart, onSuccess, onError) => {
      fetchUsersViewsExport(
        periodStart.format(API_DATE_FORMAT),
        period.endDate.format(API_DATE_FORMAT),
        token,
        projectSlug,
        onSuccess,
        onError,
        t,
      );
    });

  const exportOptionList = [
    {
      label: t("editor-analytics:usersExport"),
      fetchCsv: fetchUsersCsv,
      fileName: "users.csv",
    },
    {
      label: t("editor-analytics:fullViewsExport"),
      fetchCsv: fetchUsersViewsCsv,
      fileName: "users_views.csv",
    },
  ];

  const handleImportUsers = (
    file: File,
    type: string,
    notifications: unknown,
  ): void => {
    setImportError("");
    dispatch(
      apiUsersImport(
        { file, type, notifications },
        token,
        projectSlug,
        (err: { details?: { message?: string } }) =>
          setImportError(t(err?.details?.message ?? "errorUpload")),
        (res: ImportedUser[]) => {
          reloadUsers();
          setImportedUsers(res);
          setImportError("");
        },
      ),
    );
  };

  const handleCleanImport = (): void => {
    setImportedUsers([]);
    setImportError("");
  };

  const breadcrumbs = [
    { name: t("editor-menu:analytics"), url: "/users" },
    { name: t("usersTitle") },
  ];

  return (
    <div className="contents" data-testid="UsersAdmin">
      <AnalyticsPage
        actions={<AnalyticsPeriodPicker onChange={setPeriod} period={period} />}
        breadcrumbList={breadcrumbs}
        heading={
          <PageTitle>
            {t("usersTitle")}
            <InfoPanel
              links={{
                en: "https://support.okast.tv/en/support/solutions/articles/103000101760-analytics-sales-subscriptions-and-unsubscriptions-churn-",
                fr: "https://support.okast.tv/fr/support/solutions/articles/103000101762-rapports-d-analyse-suivre-les-transactions-les-abonnements-et-les-d%C3%A9sabonnements",
              }}
            />
          </PageTitle>
        }
      >
        <CustomerKpiGrid />
        <SignupsAndSubscriptionsSection period={period} />
        <Card className="gap-4">
          <CardHeader>
            <CardTitle>{t("editor-analytics:registeredUsersTitle")}</CardTitle>
            <CardAction className="flex items-center gap-2">
              <ExportCsvButton
                disabled={!canExport}
                options={exportOptionList}
              />
              {canWrite ? (
                <UsersCreateButton
                  cleanImport={handleCleanImport}
                  importedUsers={importedUsers}
                  importError={importError}
                  upload={handleImportUsers}
                />
              ) : null}
            </CardAction>
          </CardHeader>
          <CardContent>
            <DataTable
              columns={columnList}
              data={userTable.data}
              emptyText={t("editor-analytics:noDatas")}
              getRowHref={(user) => getUserHref(user, projectSlug)}
              isError={userTable.isError}
              isLoading={userTable.isLoading}
              onRetry={userTable.onRetry}
              rowCount={userTable.rowCount}
              serverState={serverState}
            />
          </CardContent>
        </Card>
      </AnalyticsPage>
    </div>
  );
}

export default UsersAdmin;
